import React, { useEffect, useState } from "react";
import { api, fmtGNF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Plus, X, Loader2, CheckCircle2, XCircle, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const FEE_TYPES = {
  registration: "Inscription", tuition: "Scolarité", exam: "Examens",
  books: "Manuels", uniform: "Uniforme", canteen: "Cantine",
  transport: "Transport", activities: "Activités parascolaires",
  insurance: "Assurance", other: "Autres",
};
const RECURRENCE = { one_time: "Une fois", monthly: "Mensuel", trimester: "Trimestriel", annual: "Annuel" };
const STATUS_UI = {
  pending_validation: { label: "En attente Fondateur", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  approved: { label: "Approuvée", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejetée", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function FeeConfigsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    class_name: "", level: "Collège", academic_year: "2026-2027",
    items: [{ fee_type: "tuition", label: "Scolarité annuelle", amount_gnf: 0, mandatory: true, recurrence: "annual" }],
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const [r, u] = await Promise.all([
        api.get("/scolarite/fee-configs"),
        api.get("/auth/me"),
      ]);
      setRows(r.data);
      setMe(u.data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.class_name.trim()) return toast.error("Classe requise");
    if (form.items.length === 0) return toast.error("Au moins un poste de frais");
    const invalid = form.items.some(it => !it.label.trim() || !parseFloat(it.amount_gnf));
    if (invalid) return toast.error("Tous les postes doivent avoir libellé + montant");
    setSaving(true);
    try {
      await api.post("/scolarite/fee-configs", {
        ...form,
        items: form.items.map(it => ({ ...it, amount_gnf: parseFloat(it.amount_gnf) || 0 })),
      });
      toast.success("Grille envoyée au Fondateur pour validation");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const validate = async (id, approved) => {
    const note = window.prompt(approved ? "Note optionnelle (approbation)" : "Motif du rejet") || "";
    try {
      await api.post(`/scolarite/fee-configs/${id}/validate`, { approved, founder_note: note });
      toast.success(approved ? "Grille approuvée" : "Grille rejetée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette grille ?")) return;
    try {
      await api.delete(`/scolarite/fee-configs/${id}`);
      toast.success("Supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const isFounder = me?.role === "founder" || me?.role === "super_admin";
  const canCreate = me?.role === "super_admin" || me?.role === "admin";

  return (
    <div className="space-y-6" data-testid="fee-configs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Briefcase className="h-3 w-3" /><span>GRILLE DES FRAIS PAR CLASSE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Configuration des frais</h1>
          <p className="text-slate-500 mt-1">Le Super Admin définit les grilles. Le Fondateur valide. La Scolarité utilise les grilles approuvées.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setOpen(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-config-btn">
            <Plus className="h-4 w-4" />Nouvelle grille
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-left">Niveau</th>
                <th className="px-4 py-3 text-left">Année</th>
                <th className="px-4 py-3 text-right">Postes</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const st = STATUS_UI[r.status] || STATUS_UI.pending_validation;
                const Ico = st.icon;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fc-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.class_name}</td>
                    <td className="px-4 py-2 text-slate-600">{r.level || "—"}</td>
                    <td className="px-4 py-2">{r.academic_year}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.items?.length || 0}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtGNF(r.total_gnf)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}><Ico className="h-3 w-3" />{st.label}</span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`view-${r.id}`}>Voir</Button>
                      {isFounder && r.status === "pending_validation" && (
                        <>
                          <Button size="sm" onClick={() => validate(r.id, true)} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid={`approve-${r.id}`}>Approuver</Button>
                          <Button size="sm" variant="destructive" onClick={() => validate(r.id, false)} data-testid={`reject-${r.id}`}>Rejeter</Button>
                        </>
                      )}
                      {canCreate && r.status !== "approved" && (
                        <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><X className="h-3 w-3" /></Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune grille tarifaire</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Nouvelle grille tarifaire</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Classe *</Label>
                <Input value={form.class_name} onChange={e => setForm({ ...form, class_name: e.target.value })} placeholder="Ex : 6ème A" data-testid="fc-class" />
              </div>
              <div>
                <Label>Niveau</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="fc-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Maternelle", "Primaire", "Collège", "Lycée"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Année</Label>
                <Input value={form.academic_year} onChange={e => setForm({ ...form, academic_year: e.target.value })} data-testid="fc-year" />
              </div>
            </div>

            <div>
              <Label>Postes de frais *</Label>
              <div className="space-y-2 mt-1">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start bg-slate-50 p-2 rounded-lg">
                    <Select value={it.fee_type} onValueChange={(v) => { const items = [...form.items]; items[i].fee_type = v; setForm({ ...form, items }); }}>
                      <SelectTrigger className="col-span-3" data-testid={`item-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(FEE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-4" placeholder="Libellé" value={it.label} onChange={e => { const items = [...form.items]; items[i].label = e.target.value; setForm({ ...form, items }); }} data-testid={`item-label-${i}`} />
                    <Input className="col-span-2" type="number" placeholder="Montant" value={it.amount_gnf} onChange={e => { const items = [...form.items]; items[i].amount_gnf = e.target.value; setForm({ ...form, items }); }} data-testid={`item-amount-${i}`} />
                    <Select value={it.recurrence} onValueChange={(v) => { const items = [...form.items]; items[i].recurrence = v; setForm({ ...form, items }); }}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(RECURRENCE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="col-span-1"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, { fee_type: "other", label: "", amount_gnf: 0, mandatory: true, recurrence: "one_time" }] })} className="gap-1"><Plus className="h-3 w-3" />Ajouter un poste</Button>
              </div>
              <div className="mt-3 text-right font-bold text-[#1E3A8A]">
                Total : {fmtGNF(form.items.reduce((s, it) => s + (parseFloat(it.amount_gnf) || 0), 0))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="fc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white gap-2" data-testid="fc-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Envoyer au Fondateur</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.class_name} — {detail?.academic_year}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Libellé</th>
                    <th className="px-3 py-2 text-left">Fréquence</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items?.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2">{FEE_TYPES[it.fee_type]}</td>
                      <td className="px-3 py-2">{it.label}{it.mandatory ? "" : " (facultatif)"}</td>
                      <td className="px-3 py-2 text-slate-500">{RECURRENCE[it.recurrence]}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtGNF(it.amount_gnf)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 border-slate-300 font-bold"><td colSpan={3} className="px-3 py-2 text-right">Total</td><td className="px-3 py-2 text-right tabular-nums text-[#1E3A8A]">{fmtGNF(detail.total_gnf)}</td></tr></tfoot>
              </table>
              {detail.founder_note && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="text-xs text-slate-500 mb-1">Note Fondateur</div>
                  {detail.founder_note}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
